import { MoreHorizontal, Plus, Share2 } from 'lucide-react';
import { accentTokens, THEME_SURFACES, type BrandLogo as Logo, type ResolvedBranding } from '@kaba/shared';
import { backgroundStyle } from '../../lib/background';
import { BrandLogo } from '../BrandLogo';

const NEUTRAL = {
  light: { column: '#eceef2', line: '#dfe2e8', ink: '#1d2128', soft: '#5d6574' },
  dark: { column: '#191c21', line: '#2d3139', ink: '#e8eaee', soft: '#9aa2b1' },
};

/**
 * Vorschau mit festen Farben je Modus – unabhängig vom eigenen Hell/Dunkel der bearbeitenden Person.
 * Kopf, zwei Beispielkarten, Eingabefeld, Button, Link und Statushinweise (U01).
 */
export function BrandPreview({
  logo,
  accent,
  header,
  background,
  mode,
  device,
  name,
}: {
  logo: Logo | null;
  accent: string | null;
  header: 'neutral' | 'tinted';
  background?: ResolvedBranding['background'];
  mode: 'light' | 'dark';
  device: 'desktop' | 'mobile';
  name: string;
}) {
  const t = accentTokens(accent, mode);
  const s = THEME_SURFACES[mode];
  const n = NEUTRAL[mode];
  const mobile = device === 'mobile';
  const card = (title: string, status: 'overdue' | 'done' | null) => (
    <div style={{ background: s.panel, border: `1px solid ${n.line}`, color: n.ink }} className="rounded-lg p-2.5 text-sm shadow-sm">
      <p className="font-medium">{title}</p>
      {status && (
        <span
          className="mt-1.5 inline-flex rounded px-1.5 py-0.5 text-[11px] font-medium"
          style={status === 'overdue' ? { background: mode === 'dark' ? '#4c1d24' : '#ffe4e6', color: mode === 'dark' ? '#fecdd3' : '#9f1239' } : { background: mode === 'dark' ? '#14392b' : '#d1fae5', color: mode === 'dark' ? '#a7f3d0' : '#065f46' }}
        >
          {status === 'overdue' ? 'Überfällig' : 'Erledigt'}
        </span>
      )}
    </div>
  );
  return (
    <div
      className="overflow-hidden rounded-xl border"
      style={{ borderColor: n.line, background: s.surface, color: n.ink, maxWidth: mobile ? 360 : undefined, width: '100%', marginInline: mobile ? 'auto' : undefined }}
      aria-label={`Vorschau ${mobile ? 'Mobil' : 'Desktop'}, ${mode === 'dark' ? 'dunkel' : 'hell'}`}
      role="img"
    >
      <div className="flex items-center gap-2 px-3 py-2" style={{ background: header === 'tinted' ? t.tint : s.surface, borderBottom: `1px solid ${n.line}` }}>
        {logo && <BrandLogo logo={logo} name={`Logo von ${name}`} size={mobile ? 'preview-mobile' : 'preview-desktop'} mode={mode} />}
        <span className="truncate font-semibold">Planung 2027</span>
        <span className="ml-auto flex items-center gap-1" style={{ color: n.soft }}>
          {!mobile && (
            <span className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs" style={{ border: `1px solid ${n.line}` }}>
              <Share2 size={12} aria-hidden /> Teilen
            </span>
          )}
          <MoreHorizontal size={16} aria-hidden />
        </span>
      </div>
      <div className="flex gap-3 px-3 pt-2 text-xs font-medium" style={{ borderBottom: `1px solid ${n.line}` }}>
        {(mobile ? ['Board', 'Liste'] : ['Board', 'Liste', 'Tabelle']).map((x, i) => (
          <span key={x} className="pb-1.5" style={{ color: i === 0 ? t.text : n.soft, borderBottom: `2px solid ${i === 0 ? t.accent : 'transparent'}` }}>
            {x}
          </span>
        ))}
      </div>
      <div className={mobile ? 'p-3' : 'grid grid-cols-2 gap-3 p-3'} style={backgroundStyle(background, mode, { width: mobile ? 360 : 960 })}>
        <div className="flex flex-col gap-2 rounded-lg p-2" style={{ background: n.column }}>
          <p className="px-1 text-xs font-semibold" style={{ color: n.soft }}>
            Offen
          </p>
          {card('Eine klar lesbare Aufgabe', 'overdue')}
          <span className="inline-flex items-center gap-1 px-1 text-xs" style={{ color: n.soft }}>
            <Plus size={12} aria-hidden /> Karte hinzufügen
          </span>
        </div>
        {!mobile && (
          <div className="flex flex-col gap-2 rounded-lg p-2" style={{ background: n.column }}>
            <p className="px-1 text-xs font-semibold" style={{ color: n.soft }}>
              Erledigt
            </p>
            {card('Programm abgestimmt', 'done')}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2 px-3 pb-3">
        <div className="rounded-lg p-2.5 text-sm" style={{ background: s.panel, border: `1px solid ${n.line}` }}>
          <p className="mb-1.5 text-xs" style={{ color: n.soft }}>
            Kommentar
          </p>
          <div className="rounded-md px-2 py-1.5 text-sm" style={{ border: `2px solid ${t.accent}`, outline: `2px solid ${t.text}`, outlineOffset: 2, color: n.soft }}>
            Eingabe mit Fokus
          </div>
          <div className="mt-2.5 flex flex-wrap items-center gap-3">
            <span className="inline-flex min-h-8 items-center rounded-lg px-3 text-sm font-medium" style={{ background: t.accent, color: t.onAccent }}>
              Speichern
            </span>
            <span className="text-sm font-medium underline" style={{ color: t.text }}>
              Link
            </span>
            <span className="rounded-md px-2 py-0.5 text-xs" style={{ background: t.soft, color: n.ink }}>
              Ausgewählt
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
